import React, { Fragment, useState } from 'react'
import PropTypes from 'prop-types'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { isEmpty } from 'lodash'
import { Skeleton } from '@material-ui/lab'
import {
  Box,
  Button,
  CircularProgress,
  colors,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core'

import {
  Menu as MenuIcon,
  Grid as GridButton,
  Search as SearchIcon,
} from 'react-feather'

import { ActivitiesTable, ActivityBoard } from './components'

import helpers from 'helpers'

import theme from 'theme'

import useSearch from 'hooks/useSearch'

import constants from 'constants/index'
import useStyles from './styles'

const LISTING_TYPE_KEY = 'governanceProjectBoard:listingType'

const ProjectBoard = ({
  questionnaires,
  governanceProject,
  requestIsLoading,
}) => {
  const {
    ALL_CATEGORY_ID,
    BOARD_CATEGORIES_BUTTONS,
    CONTESTATION_BOARD_CATEGORIES_BUTTONS,
    COMPLETED_STATUS_ID,
    REVIEW_BOARD_CATEGORIES_BUTTONS,
    WITHOUT_CODE_LABEL,
  } = constants.governanceProjectActivity

  const [currentTab, setCurrentTab] = useState(ALL_CATEGORY_ID)
  const [listingType, setListingType] = useState(
    () => sessionStorage.getItem(LISTING_TYPE_KEY) || 'board',
  )
  const [searchQuery, setSearchQuery] = useState('')

  const { searchInput, handleSearchValue, isSearching } = useSearch(
    (value) => setSearchQuery(value),
    300,
  )
  // derivado consumido pelo hook abaixo
  const underContestation = governanceProject?.underContestation
  const classes = useStyles(underContestation)()

  const underContestationAudit = governanceProject?.underContestationAudit
  const isCoveredByReview = !!governanceProject?.coveredByReview

  const categories =
    underContestation || underContestationAudit
      ? CONTESTATION_BOARD_CATEGORIES_BUTTONS?.filter(
          (category) =>
            (underContestation && category.id !== COMPLETED_STATUS_ID) ||
            underContestationAudit,
        )
      : isCoveredByReview
      ? REVIEW_BOARD_CATEGORIES_BUTTONS
      : BOARD_CATEGORIES_BUTTONS

  const { statusColors } = helpers.governanceProject.getStatusBoardPalette({
    isUnderContestation: underContestation || underContestationAudit,
    isCoveredByReview,
  })

  const handleSelectCategory = (categoryId) => {
    if (isCoveredByReview) {
      helpers.mixpanel.track('Projeto de governança', 'filtro-status-projeto', {
        governanceProjectId: governanceProject?.id,
        status: categoryId,
      })
    }
    setCurrentTab(categoryId)
  }

  const handleListingType = (type) => {
    sessionStorage.setItem(LISTING_TYPE_KEY, type)
    setListingType(type)
  }

  const filterActivitiesByStatus = (activities, questionnaire) =>
    activities?.filter((activity) => {
      const activityStatus =
        underContestation || (underContestationAudit && activity?.contestation)
          ? activity?.contestationStatus
          : activity?.status
      const matchesTab = helpers.governanceProject.isActivityInBoardTab({
        activityStatus,
        currentTab,
        isCoveredByReview,
      })
      const query = searchQuery.toLowerCase()
      const matchesSearch =
        !searchQuery ||
        activity?.theme?.toLowerCase().includes(query) ||
        activity?.code?.toLowerCase().includes(query) ||
        questionnaire?.kind?.toLowerCase().includes(query)
      return matchesTab && matchesSearch
    })

  return (
    <Fragment>
      <Box className={classes.gridContainer}>
        {categories.map((category, index) => {
          const Icon = category?.icon
          const bgColor = statusColors[category?.id] || colors.grey[500]
          const isSelected = category?.id === currentTab

          if (requestIsLoading) {
            return (
              <Skeleton
                key={index}
                variant="rect"
                animation="wave"
                width="15vw"
                height={40}
              />
            )
          }

          return (
            <Box key={index} className={classes.gridItem}>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                className={classes.categoryButton}
                onClick={() => handleSelectCategory(category?.id)}
                {...(isSelected && {
                  style: {
                    borderColor: theme.palette.primary.main,
                  },
                })}
              >
                <Box className={classes.containerButton}>
                  <Box display="flex" justifyContent="center" width="100%">
                    <Typography
                      variant="button"
                      className={classes.categoryName}
                    >
                      {category?.name}
                    </Typography>
                  </Box>
                  {!!Icon && (
                    <Box className={classes.containerIcon} bgcolor={bgColor}>
                      <Icon
                        size={20}
                        strokeWidth={2.5}
                        className={classes.categoryIcon}
                      />
                    </Box>
                  )}
                </Box>
              </Button>
            </Box>
          )
        })}
      </Box>
      <Box className={classes.viewToggleContainer}>
        <TextField
          variant="outlined"
          placeholder="Buscar por atividade, código ou questionário"
          value={searchInput}
          onChange={handleSearchValue}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon size={20} />
              </InputAdornment>
            ),
            endAdornment: isSearching && (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ),
          }}
        />
        <Box display="flex" flexShrink={0}>
          <IconButton
            onClick={() => handleListingType('table')}
            className={listingType === 'table' ? classes.activeViewButton : ''}
          >
            <MenuIcon size={22} />
          </IconButton>
          <IconButton
            onClick={() => handleListingType('board')}
            className={listingType === 'board' ? classes.activeViewButton : ''}
          >
            <GridButton size={22} />
          </IconButton>
        </Box>
      </Box>
      {listingType === 'table' ? (
        <ActivitiesTable
          questionnaires={questionnaires}
          governanceProject={governanceProject}
          currentTab={currentTab}
          searchQuery={searchQuery}
          requestIsLoading={requestIsLoading}
        />
      ) : (
        <Box>
          <PerfectScrollbar
            options={{
              wheelPropagation: false,
              useBothWheelAxes: true,
              suppressScrollY: true,
            }}
            className={classes.boardScrollbar}
          >
            <Box display="flex" gridGap={theme.spacing(2)}>
              {requestIsLoading
                ? [...Array(4)].map((_, index) => (
                    <Skeleton
                      key={index}
                      variant="rect"
                      animation="wave"
                      width={384}
                      height={599}
                    />
                  ))
                : questionnaires?.map((questionnaire) => {
                    const filteredActivities = filterActivitiesByStatus(
                      questionnaire?.governanceProjectActivities,
                      questionnaire,
                    )

                    const sortedActivities = filteredActivities
                      ?.slice()
                      .sort((a, b) => {
                        const codeCompare = (a.code || '').localeCompare(
                          b.code || '',
                          undefined,
                          { numeric: true, sensitivity: 'base' },
                        )
                        if (codeCompare !== 0) return codeCompare
                        return (a.theme || '').localeCompare(
                          b.theme || '',
                          undefined,
                          { numeric: true, sensitivity: 'base' },
                        )
                      })

                    return (
                      <Box
                        key={questionnaire?.id}
                        height={600}
                        display="flex"
                        flexDirection="column"
                      >
                        <Box className={classes.boardHeader}>
                          <Typography variant="h5">
                            {`[${questionnaire?.code || WITHOUT_CODE_LABEL}] ${
                              questionnaire?.kind
                            }`}
                          </Typography>
                        </Box>
                        <Box className={classes.boardContent}>
                          <PerfectScrollbar
                            options={{
                              wheelPropagation: true,
                              useBothWheelAxes: true,
                            }}
                            className={classes.scrollbar}
                          >
                            <Box className={classes.boardBody}>
                              {isEmpty(filteredActivities) ? (
                                <Paper
                                  variant="outlined"
                                  className={classes.notFoundActivity}
                                >
                                  <Typography>
                                    Nenhuma atividade foi encontrada.
                                  </Typography>
                                </Paper>
                              ) : (
                                sortedActivities?.map((activity) => (
                                  <ActivityBoard
                                    key={activity?.id}
                                    governanceProjectId={governanceProject?.id}
                                    activity={activity}
                                    underContestation={underContestation}
                                    isCoveredByReview={
                                      !!questionnaire?.coveredByReview
                                    }
                                    underContestationAudit={
                                      underContestationAudit
                                    }
                                  />
                                ))
                              )}
                            </Box>
                          </PerfectScrollbar>
                        </Box>
                      </Box>
                    )
                  })}
            </Box>
          </PerfectScrollbar>
        </Box>
      )}
    </Fragment>
  )
}

ProjectBoard.propTypes = {
  questionnaires: PropTypes.arrayOf(PropTypes.object),
  governanceProject: PropTypes.object,
  requestIsLoading: PropTypes.bool.isRequired,
}

export default ProjectBoard
